import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'pantheon-theme';

/**
 * Resolve the initial theme. Must mirror the inline boot script in index.html so the
 * class on <html> set before paint matches what React renders (no theme flash).
 */
export function getStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function getInitialTheme(): Theme {
  return getStoredTheme() ?? systemTheme();
}

function applyThemeClass(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Keep <html> class in sync with state.
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  // Persist + best-effort backend sync. Backend failure must stay silent and non-blocking.
  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage unavailable — keep in-memory preference */
      }
      if (isLoggedIn && user?.id) {
        // PATCH /users/me/preferences with { themePreference } (#408). Never block the UI on it.
        api.updateThemePreference(next).catch(() => {});
      }
    },
    [isLoggedIn, user?.id],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // On login / profile load, prefer the backend-saved value when present. Keyed on user id so a
  // local toggle (which does not re-fetch preferences) does not get reverted mid-session.
  const syncedUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      syncedUserRef.current = null;
      return;
    }
    if (syncedUserRef.current === user.id) return;
    syncedUserRef.current = user.id;
    const remote = user.themePreference;
    if (remote === 'light' || remote === 'dark') {
      setThemeState(remote);
      try {
        localStorage.setItem(STORAGE_KEY, remote);
      } catch {
        /* ignore */
      }
    }
  }, [isLoggedIn, user?.id, user?.themePreference]);

  // Follow OS changes only while the user has made no explicit choice.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!getStoredTheme()) setThemeState(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
